import { routes } from "pages/routes";
import type { Location } from "react-router-dom";
import type { StudentOnboardingStatus } from "services/student/model";
import type { EnhancedUser } from "services/users/utils";

export type SetupStep = "about" | "goals" | "availability";

// Who is held on the setup pages until they finish them: a student in `setup`, and
// any student past it with an answer missing.
export function isSetupDue(user: EnhancedUser) {
  return isInSetupStatus(user) || user.isOnboardingRequired === true;
}

// Managers and students who still owe a purchase are never held in setup, whatever
// their status.
export function isInSetupStatus(user: EnhancedUser) {
  return user.student?.status === "setup" && !user.isManager && !user.isPurchaseRequired;
}

function getSetupSteps(user: EnhancedUser): SetupStep[] {
  return user.isOneOnOne ? ["about", "goals", "availability"] : ["about", "goals"];
}

export function isLastSetupStep(step: SetupStep, user: EnhancedUser) {
  return getSetupSteps(user).at(-1) === step;
}

// Saving this step is what moves the student out of `setup`.
export function completesSetup(step: SetupStep, user: EnhancedUser) {
  return isLastSetupStep(step, user) && isInSetupStatus(user);
}

/**
 * The step a student in setup is due, or null for one who is not in setup. A
 * student in `setup` who has given every answer is due the last step, since its
 * save is what completes the setup.
 */
export function getNextSetupStep(user: EnhancedUser): SetupStep | null {
  const status = user.student?.onboarding_status;
  if (!status || !isSetupDue(user)) {
    return null;
  }
  const pages = getSetupSteps(user);
  const missing = pages.find(step => !isSetupStepDone(step, user, status));
  if (missing) {
    return missing;
  }
  return isInSetupStatus(user) ? (pages.at(-1) ?? null) : null;
}

function isSetupStepDone(
  step: SetupStep,
  user: EnhancedUser,
  status: StudentOnboardingStatus
) {
  switch (step) {
    case "about":
      return (
        getAboutYouFlags(user).every(flag => status[flag] === true) &&
        Boolean(user.profile.first_name.trim()) &&
        Boolean(user.profile.last_name?.trim())
      );
    case "goals":
      return status.has_goals;
    case "availability":
      // `has_timezone` is always true; only the address says whether a location was
      // given.
      return (
        status.has_availability === true &&
        Boolean(user.profile.address.city) &&
        Boolean(user.profile.address.country)
      );
  }
}

function getAboutYouFlags(user: EnhancedUser): (keyof StudentOnboardingStatus)[] {
  const flags: (keyof StudentOnboardingStatus)[] = [
    "has_native_language",
    "has_industry",
    "has_title",
    "has_level",
  ];
  if (user.isOneOnOne) {
    flags.push("has_learning_reasons", "has_management_level", "has_job_category");
  }
  return flags;
}

export function getSetupStepPath(step: SetupStep) {
  switch (step) {
    case "about":
      return routes.setupAbout.path();
    case "goals":
      return routes.setupGoals.path();
    case "availability":
      return routes.setupAvailability.path();
  }
}

export function isSetupPath(pathname: string) {
  return routes.setup.match(pathname);
}

// Whether a step of the setup is open to a student due `nextStep`: that step and
// the ones before it, which a student has just saved or goes back to.
export function isSetupStepOpen(
  pathname: string,
  nextStep: SetupStep,
  user: EnhancedUser
) {
  const pages = getSetupSteps(user);
  const openPages = pages.slice(0, pages.indexOf(nextStep) + 1);
  return openPages.some(step => getSetupStepPath(step) === pathname);
}

// Signing out, a sign-in link that may sign the browser in as someone else, and a
// lesson under way stay reachable from inside setup.
const UNGATED_ROUTES = [
  routes.signOut,
  routes.public.signInLink,
  routes.sessionConversation,
  routes.postFeedback,
];

type SetupRedirect = {
  to: string;
  state: unknown;
};

/**
 * Where a student due a setup page is sent instead of the page they asked for,
 * or null where they may stay. They are held on the setup pages until they finish
 * them; the address they were going to travels along, for the last save to
 * return them to.
 */
export function getSetupRedirect(
  user: EnhancedUser,
  location: Pick<Location, "pathname" | "search" | "hash" | "state">
): SetupRedirect | null {
  const nextStep = getNextSetupStep(user);
  if (
    !nextStep ||
    UNGATED_ROUTES.some(route => route.match(location.pathname)) ||
    isSetupStepOpen(location.pathname, nextStep, user)
  ) {
    return null;
  }
  return {
    to: getSetupStepPath(nextStep),
    state: isSetupPath(location.pathname)
      ? location.state
      : { setupLeaveTo: location.pathname + location.search + location.hash },
  };
}

export function readSetupLeaveTo(state: unknown) {
  if (typeof state === "object" && state !== null && "setupLeaveTo" in state) {
    const leaveTo = state.setupLeaveTo;
    return typeof leaveTo === "string" ? leaveTo : null;
  }
  return null;
}
